import React, { useEffect, useState } from 'react';
import PartnerLayout from '../../components/layout/PartnerLayout';
import { Card } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { ListFilter, Clock, CheckCircle2, Upload, Loader2, RefreshCw } from 'lucide-react';
import { partnerAPI } from '../../services/api';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle
} from '../../components/ui/dialog';
import { Label } from '../../components/ui/label';

const TASK_STATUS_CONFIG = {
  PENDING: { label: 'Pending', color: 'bg-yellow-100 text-yellow-700' },
  IN_PROGRESS: { label: 'In Progress', color: 'bg-blue-100 text-blue-700' },
  SUBMITTED: { label: 'Submitted', color: 'bg-indigo-100 text-indigo-700' },
  UNDER_REVIEW: { label: 'Under Review', color: 'bg-purple-100 text-purple-700' },
  REVISION_REQUIRED: { label: 'Revision Required', color: 'bg-orange-100 text-orange-700' },
  COMPLETED: { label: 'Completed', color: 'bg-green-100 text-green-700' }
};

export default function PartnerTasks() {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({ total: 0, totalPages: 1 });
  const [statusFilter, setStatusFilter] = useState('');

  // Submit dialog
  const [submitOpen, setSubmitOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [submitText, setSubmitText] = useState('');

  useEffect(() => {
    fetchTasks();
  }, [page, statusFilter]);

  const fetchTasks = async () => {
    try {
      setLoading(true);
      const params = { page, limit: 20 };
      if (statusFilter) params.status = statusFilter;
      const response = await partnerAPI.getTasks(params);
      setTasks(response.data);
      setPagination(response.pagination);
    } catch (error) {
      toast.error('Failed to fetch tasks');
    } finally {
      setLoading(false);
    }
  };

  const openSubmitDialog = (task) => {
    setSelectedTask(task);
    setSubmitText('');
    setSubmitOpen(true);
  };

  const handleSubmit = async () => {
    if (!submitText.trim()) {
      toast.error('Please provide a submission text');
      return;
    }

    setSubmitting(true);
    try {
      await partnerAPI.submitTask(selectedTask.taskId, submitText);
      toast.success('Task submitted successfully!');
      setSubmitOpen(false);
      fetchTasks();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to submit task');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <PartnerLayout>
      <div className="space-y-6">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">My Tasks</h1>
            <p className="mt-1 text-slate-600">Tasks assigned to you by counselors</p>
          </div>
          <Button
            variant="outline"
            onClick={fetchTasks}
            disabled={loading}
            className="partner-secondary-btn rounded-2xl"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>

        {/* Status Filter */}
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => { setStatusFilter(''); setPage(1); }}
            className={`rounded-full px-3 py-1.5 text-sm transition-colors ${
              !statusFilter ? 'partner-chip partner-chip-active' : 'partner-chip'
            }`}
          >
            All
          </button>
          {Object.entries(TASK_STATUS_CONFIG).map(([key, config]) => (
            <button
              key={key}
              onClick={() => { setStatusFilter(key); setPage(1); }}
              className={`rounded-full px-3 py-1.5 text-sm transition-colors ${
                statusFilter === key ? 'partner-chip partner-chip-active' : 'partner-chip'
              }`}
            >
              {config.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-48">
            <div className="w-10 h-10 border-4 border-purple-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : tasks.length === 0 ? (
          <Card className="partner-card rounded-[28px] p-12 text-center">
            <ListFilter className="mx-auto mb-3 h-12 w-12 text-[#A87DFA]" />
            <h3 className="text-lg font-semibold text-slate-900">No tasks assigned</h3>
            <p className="mt-1 text-sm text-slate-600">Tasks from counselors will appear here</p>
          </Card>
        ) : (
          <div className="space-y-3">
            {tasks.map((task) => {
              const statusConf = TASK_STATUS_CONFIG[task.status] || TASK_STATUS_CONFIG.PENDING;
              const canSubmit = ['PENDING', 'IN_PROGRESS', 'REVISION_REQUIRED'].includes(task.status);
              return (
                <Card key={task.taskId} className="partner-card rounded-[26px] p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-1">
                        <h3 className="font-medium text-slate-900">{task.title}</h3>
                        <Badge className={statusConf.color}>{statusConf.label}</Badge>
                        {task.priority && (
                          <Badge variant="outline" className="text-xs">{task.priority}</Badge>
                        )}
                      </div>
                      <p className="mb-2 text-sm text-slate-600">{task.description}</p>
                      {task.instructions && (
                        <p className="mb-2 text-xs text-slate-500">Instructions: {task.instructions}</p>
                      )}
                      <div className="flex items-center gap-3 text-xs text-slate-500">
                        <span>Type: {task.taskType}</span>
                        {task.dueDate && <span>Due: {new Date(task.dueDate).toLocaleDateString()}</span>}
                      </div>
                      {task.feedback?.text && (
                        <div className="mt-2 rounded-2xl border border-[#A87DFA]/14 bg-white/40 p-2 text-sm text-[#6D49D4]">
                          Feedback: {task.feedback.text}
                        </div>
                      )}
                    </div>
                    {canSubmit && (
                      <Button
                        size="sm"
                        onClick={() => openSubmitDialog(task)}
                        className="partner-primary-btn ml-4 rounded-2xl"
                      >
                        <Upload className="w-3 h-3 mr-1" /> Submit
                      </Button>
                    )}
                  </div>
                </Card>
              );
            })}
          </div>
        )}

        {/* Pagination */}
        {pagination.totalPages > 1 && (
          <div className="flex items-center justify-center gap-2">
            <button
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
              className="partner-secondary-btn rounded-2xl px-3 py-1.5 text-sm disabled:opacity-50"
            >
              Previous
            </button>
            <span className="text-sm text-slate-600">Page {page} of {pagination.totalPages}</span>
            <button
              onClick={() => setPage(p => Math.min(pagination.totalPages, p + 1))}
              disabled={page === pagination.totalPages}
              className="partner-secondary-btn rounded-2xl px-3 py-1.5 text-sm disabled:opacity-50"
            >
              Next
            </button>
          </div>
        )}
      </div>

      {/* Submit Task Dialog */}
      <Dialog open={submitOpen} onOpenChange={setSubmitOpen}>
        <DialogContent className="partner-dialog max-w-md rounded-[28px] p-0 flex flex-col max-h-[85vh]">
          <DialogHeader className="px-6 pt-6 pb-3 shrink-0">
            <DialogTitle>Submit Task: {selectedTask?.title}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 px-6 py-2 overflow-y-auto flex-1">
            <div>
              <Label>Your Submission</Label>
              <textarea
                value={submitText}
                onChange={(e) => setSubmitText(e.target.value)}
                placeholder="Describe your work or provide the requested information..."
                className="mt-1 min-h-[120px] w-full rounded-2xl px-3 py-2 text-sm"
              />
            </div>
          </div>
          <div className="px-6 py-4 border-t shrink-0 bg-white rounded-b-[28px]">
            <div className="flex justify-end gap-2">
              <Button variant="outline" className="partner-secondary-btn rounded-2xl" onClick={() => setSubmitOpen(false)}>Cancel</Button>
              <Button
                onClick={handleSubmit}
                disabled={submitting}
                className="partner-primary-btn rounded-2xl"
              >
                {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
                Submit
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </PartnerLayout>
  );
}
